import { Col, Container, Modal, Row, Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

import styles from './styles.module.scss';
import { ConfirmationDialogUtils } from './utils';
import TextInput from '../item/TextInput';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useRef } from 'react';
import { ErrorLabel } from '../forms';

/**
 * ConfirmationDialog.
 * @param {Object} need to have "entity" (event, flag, etc), "onConfirm" (function to do on YES click), "onCancel" (function to do on NO click)
 * and can have "customDelMessage" and "customTitle" to customize title and body texts.
 * @returns {ReactElement} returns JSX with the component ConfirmationDialog.
 */

const ConfirmationDialog = ({
	entity,
	onConfirm,
	onCancel,
	customDelMessage,
	customTitle,
	showReasonForDelete,
	showReasonForDeactivate
}) => {
	const { buildTitle, getModalBody } = ConfirmationDialogUtils();
	const formikForm = useRef(null);
	const VALIDATION_SCHEMA = Yup.object().shape({
		reasonForDelete: showReasonForDelete
			? Yup.string()
					.trim()
					.required('Reason for Deletion is required')
					.max(255, 'Maximum 255 characters allowed')
			: Yup.string().nullable(),

		reasonForDeactivate: showReasonForDeactivate
			? Yup.string()
					.trim()
					.required('Reason for Deactivate is required')
					.max(255, 'Maximum 255 characters allowed')
			: Yup.string().nullable()
	});

	const INITIAL_VALUES = {
		reasonForDelete: '',
		reasonForDeactivate: ''
	};

	const handleSubmitForm = (values, { setSubmitting, resetForm }) => {
		const payload = {};
		if (showReasonForDelete) {
			payload.reasonForDelete = values.reasonForDelete;
		}
		if (showReasonForDeactivate) {
			payload.reasonForDeactivate = values.reasonForDeactivate;
		}
		onConfirm(payload);
		setSubmitting(false);
		resetForm();
	};

	return (
		<Modal
			show
			onHide={onCancel}
			backdrop="static"
			keyboard={false}
			size="md"
			aria-labelledby="confirmation-modal-title"
			centered
		>
			<Modal.Header closeButton closeLabel={`${buildTitle({ entity, customTitle })} Close Dialog`}>
				<Modal.Title className={styles.title} id="confirmation-modal-title">
					{buildTitle({ entity, customTitle })}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Container>
					<Row className="align-items-center">
						<Col xs={2}>
							<h1 className="text-danger">
								<i className="fas fa-exclamation-circle"></i>
							</h1>
						</Col>
						<Col>
							{getModalBody({
								entity,
								customDelMessage
							})}
						</Col>
					</Row>
					<Row className="mt-3">
						<Formik
							validationSchema={VALIDATION_SCHEMA}
							initialValues={INITIAL_VALUES}
							onSubmit={handleSubmitForm}
							innerRef={formikForm}
						>
							{({ errors, touched, resetForm, handleSubmit, handleChange, values: val }) => (
								<Form
									id="confirmationDialogForm"
									aria-label={'confirmationDialogForm'}
									onSubmit={handleSubmit}
								>
									<Col md={12} lg={12} xl={12} className="mb-3">
										{showReasonForDelete && (
											<>
												<TextInput
													id="reasonForDelete"
													name="reasonForDelete"
													type="textarea"
													label="Reason for Delete"
													onChange={handleChange}
													value={val.reasonForDelete}
													placeholder={`Reason for delete`}
													className={
														errors.reasonForDelete &&
														touched.reasonForDelete &&
														'is-invalid form-control'
													}
													required
													limitCharacters={255}
												/>
												<ErrorLabel
													errors={errors}
													touched={touched}
													value={'reasonForDelete'}
													small
												/>
											</>
										)}
										{showReasonForDeactivate && (
											<>
												<TextInput
													id="reasonForDeactivate"
													name="reasonForDeactivate"
													type="textarea"
													label="Reason for Deactivate"
													onChange={handleChange}
													value={val.reasonForDeactivate}
													placeholder={`Reason for Deactivate`}
													className={
														errors.reasonForDeactivate &&
														touched.reasonForDeactivate &&
														'is-invalid form-control'
													}
													required
													limitCharacters={255}
												/>
												<ErrorLabel
													errors={errors}
													touched={touched}
													value={'reasonForDeactivate'}
													small
												/>
											</>
										)}
									</Col>
								</Form>
							)}
						</Formik>
					</Row>
				</Container>
			</Modal.Body>
			<Modal.Footer>
				<Button
					onClick={() => formikForm.current.handleSubmit()}
					variant="primary"
					className={styles.button}
				>
					Yes
				</Button>
				<Button onClick={onCancel} variant={'secondary'} className={styles.button}>
					No
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

ConfirmationDialog.propTypes = {
	entity: PropTypes.string,
	onConfirm: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	customTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	customDelMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	showReasonForDelete: PropTypes.bool,
	showReasonForDeactivate: PropTypes.bool
};

export default ConfirmationDialog;
